import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { convertToTestIdFormat } from '@/utils/stringUtils';
import { Tooltip } from '@/shared/components/Tooltip';

type RadioInputBox = {
  id: string;
  text: string;
  subText?: string;
  value: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
};

export type { RadioInputBox };

export interface GroupRadioButtonProps {
  dataTestId: string;
  id: string;
  options: RadioInputBox[];
  field: ControllerRenderProps<any, any>;
  // When provided, overrides the value-based fallback so duplicate-value options
  // can still be distinguished by which one the user clicked.
  selectedId?: string;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
  useDrawerStyles?: boolean;
  // Controls subText positioning: 'inline' renders beside the label, 'stacked' renders below it.
  layout?: 'inline' | 'stacked';
  // Marks the group as required for assistive tech (sets `aria-required`).
  required?: boolean;
  styles?: {
    options?: {
      spacing?: number;
      height?: number;
    };
  };
}

const DEFAULT_DISABLED_TOOLTIP = '';

export function GroupRadioButton({
  id,
  dataTestId,
  options,
  field,
  selectedId: selectedIdProp,
  disabled,
  disabledTooltip,
  useDrawerStyles = false,
  layout = 'inline',
  required = false,
  styles,
}: GroupRadioButtonProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isItemDisabled = (itemDisabled?: boolean) => disabled || itemDisabled;

  // Track selection by id so options with duplicate values don't all highlight.
  // Prefer the prop (set by callers that carry the clicked id in form state);
  // fall back to first-value-match for pre-fill and simple distinct-value usage.
  const selectedId = selectedIdProp ?? options.find((o) => o.value === field.value)?.id ?? '';
  const isItemSelected = (itemId: string) => selectedId === itemId;

  const getDisabledTooltipText = (itemDisabledTooltip?: React.ReactNode) =>
    itemDisabledTooltip || disabledTooltip || DEFAULT_DISABLED_TOOLTIP;

  const getBackgroundColor = (itemDisabled?: boolean, itemId?: string) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[50];
    }
    if (isItemSelected(itemId || '')) {
      return 'rgba(61, 177, 87, 0.10)';
    }
    return undefined;
  };

  const getBorderColor = (itemDisabled?: boolean, itemId?: string) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[200];
    }
    if (isItemSelected(itemId || '')) {
      return useDrawerStyles ? theme.palette.neutralGrey[950] : theme.palette.primary.main;
    }
    // Drawer styles use neutralGrey[200], original uses black
    return useDrawerStyles ? theme.palette.neutralGrey[200] : 'black';
  };

  // Radios are now keyed by id; translate back to value so parent's wrapped
  // onChange still receives `(event, value, id)`.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, clickedId: string) => {
    const option = options.find((o) => o.id === clickedId);
    if (!option) {
      return;
    }
    field.onChange(event, option.value, option.id);
  };

  const getTextColor = (itemDisabled?: boolean) => {
    return isItemDisabled(itemDisabled)
      ? theme.palette.neutralGrey[400]
      : theme.palette.neutralGrey[950];
  };

  const getSubTextColor = (itemDisabled?: boolean) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[300];
    }
    // Drawer styles use neutralGrey[700] for subtext, original uses same as text
    return useDrawerStyles ? theme.palette.neutralGrey[700] : getTextColor(itemDisabled);
  };

  const renderItemContent = (item: RadioInputBox) => (
    <Box
      sx={{
        border: `1px solid ${getBorderColor(item.disabled, item.id)}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
        height: styles?.options?.height ?? 'auto',
        // Drawer styles use different padding
        ...(useDrawerStyles ? { py: '12px', pl: '8px', pr: '16px' } : { p: 2 }),
        textAlign: 'left',
        backgroundColor: getBackgroundColor(item.disabled, item.id),
        cursor: isItemDisabled(item.disabled) ? 'pointer' : undefined,
        ':hover': {
          backgroundColor: isItemDisabled(item.disabled) ? undefined : 'rgba(19, 102, 62, 0.15)',
        },
      }}
    >
      <FormControlLabel
        sx={{
          width: '100%',
          '& .MuiFormControlLabel-label': {
            width: '100%',
          },
          // Drawer styles add height and margin
          ...(useDrawerStyles && { height: '40px', margin: 0 }),
        }}
        disabled={isItemDisabled(item.disabled)}
        id={convertToTestIdFormat(item.id)}
        value={item.id}
        control={
          <Radio
            id={`id_${convertToTestIdFormat(item.id)}`}
            sx={{
              color: `${
                !isItemDisabled(item.disabled) && isItemSelected(item.id)
                  ? theme.palette.green[700]
                  : useDrawerStyles
                    ? theme.palette.neutralGrey[400]
                    : theme.palette.neutralGrey[300]
              } !important`,
              // Drawer styles add padding
              ...(useDrawerStyles && { padding: 1, lineHeight: '22px' }),
            }}
          />
        }
        label={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width={'100%'}
            maxWidth={'100%'}
          >
            <Box
              display="flex"
              justifyContent={layout === 'stacked' ? 'flex-start' : 'space-between'}
              flexDirection={layout === 'stacked' ? 'column' : 'row'}
              width={'100%'}
              maxWidth={'100%'}
            >
              <Typography
                sx={{
                  color: getTextColor(item.disabled),
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '22px',
                }}
              >
                {item.text}
              </Typography>
              {item.subText ? (
                <Typography
                  variant={
                    layout === 'stacked' ? 'helpertext' : useDrawerStyles ? 'body2' : undefined
                  }
                  sx={{ color: getSubTextColor(item.disabled) }}
                >
                  {item.subText}
                </Typography>
              ) : null}
            </Box>
            {isItemDisabled(item.disabled) && item.disabledTooltip !== undefined ? (
              <IconButton disabled={true}>
                <InfoOutlinedIcon sx={{ color: theme.palette.neutralGrey[400] }} />
              </IconButton>
            ) : (
              item.tooltip && (
                <Tooltip
                  title={item.tooltip}
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: { width: '220px', alignItems: 'flex-start', textAlign: 'left' },
                    },
                  }}
                >
                  <IconButton>
                    <InfoOutlinedIcon sx={{ color: 'grey' }} />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Stack>
        }
      />
    </Box>
  );

  const disabledTooltipSlotProps = {
    tooltip: {
      sx: {
        width: '220px',
      },
    },
  };

  return (
    <RadioGroup
      data-testid={dataTestId}
      id={id}
      {...field}
      value={selectedId}
      onChange={handleChange}
      aria-labelledby={`${id}Header`}
      aria-required={required}
      // Drawer styles remove margin on last child
      sx={{
        gap: styles?.options?.spacing ?? 1.5,
        ...(useDrawerStyles ? { display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      {options.map((item) => {
        const disabledTooltipText = getDisabledTooltipText(item.disabledTooltip);

        if (isItemDisabled(item.disabled) && disabledTooltipText) {
          return (
            <Tooltip
              key={item.id}
              title={disabledTooltipText}
              placement={isMobile ? 'top' : 'right'}
              slotProps={disabledTooltipSlotProps}
            >
              {renderItemContent(item)}
            </Tooltip>
          );
        }

        return <Box key={item.id}>{renderItemContent(item)}</Box>;
      })}
    </RadioGroup>
  );
}

export function GroupRadioButtonSkeleton({
  count = 3,
  dataTestId,
  containerSx,
  itemSx,
}: {
  count?: number;
  dataTestId?: string;
  containerSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
}) {
  return (
    <Stack data-testid={dataTestId} direction="column" gap="12px" sx={containerSx}>
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          sx={{
            border: '1px solid',
            borderColor: 'neutralGrey.200',
            borderRadius: '8px',
            pl: '8px',
            pr: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: '64px',
            ...itemSx,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="40px"
            height="40px"
          >
            <Skeleton variant="circular" width={20} height={20} sx={{ flexShrink: 0 }} />
          </Box>
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      ))}
    </Stack>
  );
}
